import React, { useState } from "react";
import { makeStyles } from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

const useStyles = makeStyles(() => ({
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  wrapper: {
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    position: "relative",
    animation: "$fadeInUp 200ms ease-out 200ms both",
    marginTop: "12px",
    "&:hover $copyBtn": {
      opacity: 1,
    },
  },
  scrollContainer: {
    overflowX: "auto",
  },
  caption: {
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    background: "#fafbfc",
    padding: "14px 16px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1A1F2E",
    lineHeight: 1.4,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  thead: {},
  stickyThead: {
    "& th": {
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
  },
  th: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    color: "#fff",
    background: "#1e3a5f",
    borderBottom: "2px solid #1e3a5f",
    padding: "11px 16px",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  thRight: {
    textAlign: "right",
  },
  td: {
    padding: "11px 16px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  tdRight: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  tdNegative: {
    color: "#dc2626",
    fontWeight: 500,
  },
  tdPositiveGreen: {
    color: "#059669",
    fontWeight: 500,
  },
  row: {
    transition: "background 0.1s",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  rowEven: {
    background: "#fafbfc",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  expandBtn: {
    display: "block",
    width: "100%",
    padding: "10px 16px",
    border: "none",
    borderTop: "1px solid #e5e7eb",
    background: "#fafbfc",
    color: "#2563eb",
    fontSize: "12.5px",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    "&:hover": {
      background: "#f0f6ff",
    },
  },
  copyBtn: {
    position: "absolute",
    top: "8px",
    right: "8px",
    opacity: 0,
    transition: "opacity 150ms",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "4px 6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    "&:hover": {
      background: "#f3f4f6",
    },
  },
  copyBtnCopied: {
    background: "#f0fdf4",
    borderColor: "#86efac",
    color: "#16a34a",
    fontSize: "11px",
    fontWeight: 600,
    padding: "4px 8px",
    opacity: 1,
  },
}));

function isNumericCell(value) {
  if (typeof value !== "string") return false;
  var trimmed = value.trim();
  return /^\$/.test(trimmed) || /^-\$/.test(trimmed) || /[%MKB]$/.test(trimmed);
}

function isNegativeValue(value) {
  if (typeof value !== "string") return false;
  return value.trim().startsWith("-");
}

function isPositiveNumeric(value) {
  if (typeof value !== "string") return false;
  var trimmed = value.trim();
  return isNumericCell(trimmed) && !trimmed.startsWith("-");
}

function getCellColorClass(value, columnName, columnColors, classes) {
  var isNeg = isNegativeValue(value);
  var isPos = isPositiveNumeric(value);
  if (!columnColors || !columnColors[columnName]) {
    // Default: negative = red, no color for positive
    return isNeg ? classes.tdNegative : "";
  }
  var type = columnColors[columnName];
  if (type === "cost") {
    // cost variance: negative (spending less) = green, positive (overspending) = red
    if (isNeg) return classes.tdPositiveGreen;
    if (isPos) return classes.tdNegative;
    return "";
  }
  // revenue type: negative = red, positive = green
  if (isNeg) return classes.tdNegative;
  if (isPos) return classes.tdPositiveGreen;
  return "";
}

var DataTable = React.memo(function DataTable({ table }) {
  var classes = useStyles();
  var _useState = useState(false);
  var expanded = _useState[0];
  var setExpanded = _useState[1];

  var _useCopied = useState(false);
  var copied = _useCopied[0];
  var setCopied = _useCopied[1];

  if (!table || !table.columns || !table.rows) return null;

  var visibleRows = expanded || table.rows.length <= 10
    ? table.rows
    : table.rows.slice(0, 10);

  var useStickyHeader = table.rows.length >= 8;

  // Detect which columns are numeric by majority vote across all rows
  var numericColumns = table.columns.map(function (col, ci) {
    if (!table.rows.length) return false;
    var numericCount = 0;
    var totalNonEmpty = 0;
    for (var ri = 0; ri < table.rows.length; ri++) {
      var val = String(table.rows[ri][ci] || "").trim();
      if (val) {
        totalNonEmpty++;
        if (isNumericCell(val)) numericCount++;
      }
    }
    return totalNonEmpty > 0 && numericCount / totalNonEmpty > 0.5;
  });

  function handleCopy() {
    var headerLine = table.columns.join("\t");
    var rowLines = table.rows.map(function (row) { return row.join("\t"); });
    var text = [headerLine].concat(rowLines).join("\n");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 1500);
    }
  }

  return (
    <div className={classes.wrapper}>
      <button
        className={copied ? classes.copyBtn + " " + classes.copyBtnCopied : classes.copyBtn}
        onClick={handleCopy}
        title="Copy table"
      >
        {copied ? "Copied!" : <FileCopyOutlinedIcon style={{ fontSize: 14, color: "#6b7280" }} />}
      </button>
      {table.caption && <div className={classes.caption}>{table.caption}</div>}
      <div className={classes.scrollContainer}>
        <table className={classes.table}>
          <thead className={useStickyHeader ? classes.stickyThead : classes.thead}>
            <tr>
              {table.columns.map(function (col, ci) {
                var thClass = classes.th + (numericColumns[ci] ? " " + classes.thRight : "");
                return <th key={ci} className={thClass}>{col}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(function (row, ri) {
              return (
                <tr key={ri} className={ri % 2 === 1 ? classes.rowEven : classes.row}>
                  {row.map(function (cell, ci) {
                    var cellStr = String(cell);
                    var colName = table.columns[ci];
                    var colorClass = getCellColorClass(cellStr, colName, table.column_colors, classes);
                    var cellClass = classes.td +
                      (numericColumns[ci] ? " " + classes.tdRight : "") +
                      (colorClass ? " " + colorClass : "");
                    return <td key={ci} className={cellClass}>{cellStr}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!expanded && table.rows.length > 10 && (
        <button
          className={classes.expandBtn}
          onClick={function () { setExpanded(true); }}
        >
          Show all {table.rows.length} rows
        </button>
      )}
      {expanded && table.rows.length > 10 && (
        <button
          className={classes.expandBtn}
          onClick={function () { setExpanded(false); }}
        >
          Show fewer rows
        </button>
      )}
    </div>
  );
});

export default DataTable;
